/**
 * modules.ts — Typed facade for vanilla JS window globals
 *
 * Provides compile-time safe access to M1–M8 and Logger without
 * (window as any) casts scattered across Svelte components.
 */

// ── M1 — Audio Engine ────────────────────────────────────────

export interface M1Type {
  play(url: string): void;
  stop(): void;
  setVolume(level: number): void;
  toggleMute(): { muted: boolean; volume: number };
  startPreview(url: string): Promise<{ success: boolean }>;
  stopPreview(): { resumed: boolean };
  isPreviewActive(): boolean;
  onMuteRampTick(callback: (vol: number) => void): void;
  onMuteChange(callback: (state: { muted: boolean; volume: number }) => void): void;
  onPlaying(callback: () => void): void;
  onPreviewChange(callback: (state: { active: boolean; url: string | null }) => void): void;
  onHlsInstance(callback: (hls: any) => void): void;
  isIOS(): boolean;
  wakeMediaSession(): { success: boolean };
  init(config: { apiBase: string | null }): void;
  _muted?: boolean;
}

// ── M2 — Station Store ───────────────────────────────────────

export interface StationEntry {
  index: number;
  url: string;
  label: string;
  locked: boolean;
  url_lq: string;
  url_hq: string;
}

export interface M2Type {
  getStations(): StationEntry[];
  setStation(index: number, url: string, label: string, url_lq?: string, url_hq?: string): void;
  removeStation(index: number): void;
  reorderStations(fromIndex: number, toIndex: number): void;
  getLastPlayedIndex(): number | null;
  setLastPlayedIndex(index: number): void;
  setLocked(index: number, locked: boolean): { success: boolean; error?: string };
  getLocked(index: number): boolean;
  setQualityPreference(quality: string): { success: boolean; error?: string };
  getQualityPreference(): string;
  isPersistent(): boolean;
  storeLocalAudio(file: File): Promise<string | null>;
  exportRoster(): object;
  importRoster(data: object): { success: boolean; error?: string };
}

// ── M3 — Playback Controller ─────────────────────────────────

export interface M3Type {
  init(): void;
  restoreSelection(): void;
  resumePlayback(): void;
  next(): void;
  previous(): void;
  playStation(index: number): void;
  getCurrentIndex(): number;
  onNavigate(callback: (index: number) => void): void;
  getPendingPlayback(): boolean;
}

// ── M4 — Recently Played Log ─────────────────────────────────

export interface M4Type {
  subscribe(metadataEngine: any): void;
  getLog(): string[];
  onLogUpdate(callback: (log: string[]) => void): () => void;
}

// ── M6 — Metadata Engine ─────────────────────────────────────

export interface TrackInfo {
  artist: string | null;
  title: string | null;
  raw: string;
  source: string;
}

export interface M6Type {
  init(apiBaseUrl: string | null): void;
  setStation(index: number, url: string): void;
  onTrackChange(callback: (track: TrackInfo) => void): void;
  getAvailability(index: number): { available: boolean; source: string | null };
  onAvailabilityChange(callback: (info: { index: number; available: boolean; source: string | null }) => void): void;
}

// ── M7 — Station Browser ─────────────────────────────────────

export interface CatalogStation {
  name: string;
  url: string;
  genre: string;
  group: string;
  quality: string;
  description: string;
  default: boolean;
  url_lq: string;
  url_hq: string;
}

export interface RosterEntry {
  slot: number;
  url: string;
  label: string;
  url_lq: string;
  url_hq: string;
}

export interface M7Type {
  init(): Promise<void>;
  getStations(): CatalogStation[];
  getGenres(): string[];
  filterByGenre(genre: string | null): CatalogStation[];
  getSurpriseRoster(): RosterEntry[];
  getFavoritesRoster(): RosterEntry[];
}

// ── M8 — Media Session Bridge ────────────────────────────────

export interface M8Type {
  init(): void;
  updateMetadata(stationName: string | null, trackInfo: { artist?: string; title?: string } | null): void;
  isIOS(): boolean;
  isMobile(): boolean;
  onStationChange(index: number): void;
  onTrackUpdate(track: TrackInfo): void;
  onMuteChanged(state: { muted: boolean; volume: number }): void;
  rearm(): void;
}

// ── Logger ────────────────────────────────────────────────────

export interface LogEntry {
  ts: number;
  module: string;
  fn: string;
  inputs?: any;
  output?: any;
  durationMs?: number;
  level?: string;
  error?: any;
}

export interface LoggerType {
  log(entry: LogEntry): void;
}

// ── Typed accessors ──────────────────────────────────────────

export const M1: M1Type | undefined = (window as any).M1;
export const M2: M2Type | undefined = (window as any).M2;
export const M3: M3Type | undefined = (window as any).M3;
export const M4: M4Type | undefined = (window as any).M4;
export const M6: M6Type | undefined = (window as any).M6;
export const M7: M7Type | undefined = (window as any).M7;
export const M8: M8Type | undefined = (window as any).M8;
export const Logger: LoggerType | undefined = (window as any).Logger;
